import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { agentService } from '../services/api/agent';
import type { AgentContext, AgentContextResponse, ToolDefinition } from '../services/api/agent/types';

interface AgentContextValue {
  organizationId: string | null;
  agentContext: AgentContext | null;
  availableTools: ToolDefinition[];
  isLoading: boolean;
  error: string | null;
  isAgentEnabled: boolean;
  setOrganizationId: (id: string | null) => void;
  refreshContext: () => Promise<void>;
}

const AgentContextCtx = createContext<AgentContextValue | null>(null);

interface AgentContextProviderProps {
  children: React.ReactNode;
  initialOrganizationId?: string;
}

export const AgentContextProvider: React.FC<AgentContextProviderProps> = ({
  children,
  initialOrganizationId
}) => {
  const [organizationId, setOrganizationId] = useState<string | null>(initialOrganizationId || null);
  const [agentContext, setAgentContext] = useState<AgentContext | null>(null);
  const [availableTools, setAvailableTools] = useState<ToolDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAgentEnabled, setIsAgentEnabled] = useState(false);

  const checkAgentEnabled = useCallback(async () => {
    try {
      const status = await agentService.getAgentStatus();
      const directAgentEnabled = status.enabled && status.agentEnabled !== false;
      setIsAgentEnabled(directAgentEnabled);
      return directAgentEnabled;
    } catch {
      setIsAgentEnabled(false);
      return false;
    }
  }, []);

  const refreshContext = useCallback(async () => {
    if (!organizationId) {
      setAgentContext(null);
      setAvailableTools([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [contextResponse, toolsResponse] = await Promise.all([
        agentService.getContext(organizationId),
        agentService.getTools(organizationId)
      ]);

      setAgentContext(contextResponse.context);
      setAvailableTools(toolsResponse.tools);
    } catch (err: any) {
      setError(err.message || 'Failed to load agent context');
      setAgentContext(null);
      setAvailableTools([]);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    checkAgentEnabled();
  }, [checkAgentEnabled]);

  useEffect(() => {
    if (isAgentEnabled && organizationId) {
      refreshContext();
    }
  }, [isAgentEnabled, organizationId, refreshContext]);

  const value = useMemo<AgentContextValue>(() => ({
    organizationId,
    agentContext,
    availableTools,
    isLoading,
    error,
    isAgentEnabled,
    setOrganizationId,
    refreshContext
  }), [
    organizationId,
    agentContext,
    availableTools,
    isLoading,
    error,
    isAgentEnabled,
    refreshContext
  ]);

  return (
    <AgentContextCtx.Provider value={value}>
      {children}
    </AgentContextCtx.Provider>
  );
};

export function useAgentContext(): AgentContextValue {
  const context = useContext(AgentContextCtx);
  if (!context) {
    throw new Error('useAgentContext must be used within an AgentContextProvider');
  }
  return context;
}

export default AgentContextProvider;
